import { Formik } from "formik";
import {
  useFormSubmit,
  Drawer as DrawerType,
  EfsMelcoEfsModelsSchemaListItem,
} from "melco-shared-logic";
import { useEffect, useState } from "react";
import { object, string } from "yup";
import { useFoldernameRules } from "../../hooks/designMappings/useFoldernameRules";
import { FoldernameRuleFormMode } from "./FoldernameRuleDrawer";
import { DisplayFoldernameRuleForm } from "./DisplayFoldernameRuleForm";
import { FOLDERNAME_RULE_TYPES } from "./SelectFoldernameRuleType";

type FoldernameRuleFormValueType = {
  type?: string;
  value?: string;
  format_detail_id?: string;
  exception_id?: string;
};

type FoldernameRuleFormProps = {
  drawer: DrawerType;
  editFoldernameRuleIndex: number | undefined;
  formMode: FoldernameRuleFormMode;
  dataAssignments: EfsMelcoEfsModelsSchemaListItem[];
};

const FoldernameRuleSchema = object().shape({
  type: string().oneOf(FOLDERNAME_RULE_TYPES),
  value: string().when("type", {
    is: "Static",
    then: (schema) => schema.required("global.error.required"),
  }),
  format_detail_id: string().when("type", {
    is: (val: string) => val !== "Static",
    then: (schema) => schema.required("global.error.required"),
  }),
});

const newFoldernameRuleInitialValues: FoldernameRuleFormValueType = {
  type: "Dynamic",
  value: "",
  format_detail_id: "",
  exception_id: "",
};

export const FoldernameRuleForm: React.FC<FoldernameRuleFormProps> = ({
  drawer,
  editFoldernameRuleIndex,
  formMode,
  dataAssignments,
}) => {
  const isAddingNewRule = editFoldernameRuleIndex == null;

  const { closeDrawer } = drawer;

  const [initialValues, setInitialValues] = useState(
    newFoldernameRuleInitialValues
  );

  const { foldernameRules, addFoldernameRule, updateFoldernameRuleAtIndex } =
    useFoldernameRules();

  useEffect(() => {
    if (isAddingNewRule) {
      setInitialValues(newFoldernameRuleInitialValues);
      return;
    }

    setInitialValues(
      (foldernameRules[
        editFoldernameRuleIndex
      ] as FoldernameRuleFormValueType) ?? newFoldernameRuleInitialValues
    );
  }, [foldernameRules, editFoldernameRuleIndex, setInitialValues]);

  const onSubmit = useFormSubmit<FoldernameRuleFormValueType>(
    async (values: FoldernameRuleFormValueType) => {
      if (isAddingNewRule) {
        addFoldernameRule(values);
      } else {
        updateFoldernameRuleAtIndex(editFoldernameRuleIndex, values);
      }

      return { successMessages: [{ message: "default" }] };
    },
    {
      translationPrefix: `designMappings.actions.foldernameRules.actions.${formMode}`,

      onSuccess: (_, { resetForm }) => {
        closeDrawer();
        resetForm({ values: initialValues });
      },
    }
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={FoldernameRuleSchema}
    >
      <DisplayFoldernameRuleForm
        dataAssignments={dataAssignments}
        formMode={formMode}
        isAddingNewRule={isAddingNewRule}
      />
    </Formik>
  );
};
